import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ScreenshotSlider — auto-advancing image carousel with dot indicators.
 * Pauses on hover. Smooth cross-fade transition via AnimatePresence.
 *
 * Props:
 *   images   — string[] — array of src paths
 *   interval — number   — ms between slides (default 3000)
 *   className — string  — extra classes on the wrapper div
 *   alt      — string   — base alt text (appended with slide index)
 *   cover    — boolean  — when true, fills parent height with object-cover images
 *                         (parent must have an explicit height, e.g. h-full)
 */
export default function ScreenshotSlider({
  images = [],
  interval = 3000,
  className = '',
  alt = 'Screenshot',
  cover = false,
}) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setCurrent(c => (c + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    const id = setInterval(next, interval);
    return () => clearInterval(id);
  }, [paused, interval, next, images.length]);

  if (!images.length) return null;

  return (
    <div
      className={`relative select-none ${cover ? 'h-full' : ''} ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slide area */}
      <div className={`relative overflow-hidden ${cover ? 'h-full' : 'rounded-2xl'}`}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.img
            key={current}
            src={images[current]}
            alt={`${alt} ${current + 1}`}
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45, ease: 'easeInOut' }}
            className={
              cover
                ? 'absolute inset-0 w-full h-full object-cover object-top'
                : 'w-full block'
            }
            draggable={false}
          />
        </AnimatePresence>
      </div>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-5 h-1.5 bg-white'
                  : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/65'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
